import { Injectable } from '@angular/core';
import { EMPTY, expand, finalize, map, Observable, of, reduce } from 'rxjs';
import { Division, PagedResponse, Team, Wrestler } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';
import { observableInSeparateThread } from '../helper/observable-helper';

/**
 * Service to get and save wrestler data.
 */
@Injectable()
export class WrestlerService {
    public static INCLUDE_DIVISION: string = 'division';
    public static INCLUDE_WEIGHT_CLASS: string = 'weightClass';
    public static INCLUDE_TEAM: string = 'team';
    public static INCLUDE_CREATED_BY_USER: string = 'createdByUserAccount';
    public static INCLUDE_WAIVERS: string = 'waivers';
    public static INCLUDE_SEEDING_CRITERIA: string = 'seedingCriteria';
    public static INCLUDE_SKILL_LEVEL: string = 'skillLevel';

    private cachedWrestlersMap: { [apiRequest: string]: PagedResponse; } = {};

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    public clearCache(): void {
        this.cachedWrestlersMap = {};
    }

    public exportToCsv(eventId: string, queryParams: string[] = null): void {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/wrestlers/csv-download`;
        url += this.http.getQueryParameters(null, queryParams);
        location.href = url;
    }

    /**
     * Get all wrestlers for the specified event
     */
    public getIndexListResponse(
        eventId: string,
        includes: string[] = null,
        queryParams: string[] = null,
        forceRefresh: boolean = true,
    ): Observable<PagedResponse> {
        queryParams = this.appendGetFields(includes, queryParams);
        queryParams.push('cache=false');
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, queryParams);
        return this.getIndexListResponseByUrl(eventId, apiRequestUrl, forceRefresh);
    }

    /**
     * Get wrestlers by a url. This is used for paging through wrestlers since the response
     * provides the proper page urls
     */
    public getIndexListResponseByUrl(
        eventId: string,
        url: string,
        forceRefresh: boolean = true,
    ): Observable<PagedResponse> {
        if (!forceRefresh &&
            this.cachedWrestlersMap[url]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedWrestlersMap[url].clone());
        }
        return this.http.get(url).pipe(map((response: IJsonApiResponse) => {
            const json: any = response;

            const wrestlers: Wrestler[] = [];
            for (const wrestler of json.data) {
                wrestlers.push((new Wrestler(eventId)).deserialize(wrestler, json.included));
            }

            const pagedResponse: PagedResponse = new PagedResponse(
                json['links']['first'],
                json['links']['last'],
                json['links']['prev'],
                json['links']['next'],
                json['meta']['total'],
                wrestlers,
            );

            this.cachedWrestlersMap[url] = pagedResponse.clone();

            return pagedResponse;
        }));
    }

    /**
     * Get all wrestlers for the specified event
     */
    public getIndex(
        eventId: string,
        includes: string[] = null,
        queryParams: string[] = null,
        forceRefresh: boolean = true,
    ): Observable<Wrestler[]> {
        return this.getIndexListResponse(eventId, includes, queryParams, forceRefresh)
            .pipe(map((listResponse: PagedResponse) => listResponse.recordList));
    }

    /**
     * Get all wrestlers. This will auto page to the next set of results so all will be returned
     */
    public getAll(eventId: string, includes: string[] = null, queryParams: string[] = null): Observable<Wrestler[]> {
        queryParams = queryParams || [];

        if (queryParams.filter((x: string) => x.indexOf('page[size]') !== -1).length === 0) {
            queryParams.push('page[size]=500');
        }

        queryParams = this.appendGetFields(null, queryParams);
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, queryParams);

        // Make the http request and recursively get all wrestlers based on the query parameters
        return this.http.get(apiRequestUrl)
            .pipe(
                expand((response: IJsonApiResponse) => {
                    const json: Object = response;

                    if (json['links'] && json['links']['next']) {
                        // If you have more values we need to call http again. This is done recursively
                        return this.http.get(json['links']['next'], false, false);
                    }
                    return EMPTY;
                }),
                map((response: IJsonApiResponse) => {
                    const wrestlers: Wrestler[] = [];
                    const included: Object[] = response.included;

                    const bodyData: any = response.data || [];
                    for (const wrestler of bodyData) {
                        wrestlers.push((new Wrestler(eventId)).deserialize(wrestler, included));
                    }

                    return wrestlers;
                }),
                reduce((previousValue: any, currentValue: any) => previousValue.concat(currentValue)),
            );
    }

    /**
     * Get a single wrestler's data
     */
    public get(eventId: string, wrestlerId: string, includes: string[] = null): Observable<Wrestler> {
        const queryParams: string[] = this.appendGetFields(includes);
        queryParams.push('cache=false');
        const apiRequestUrl: string = this.getApiUrl(eventId, wrestlerId, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(
                map((response: IJsonApiResponse) => (new Wrestler(eventId).deserialize(response, response.included))),
            );
    }

    /**
     * Get if the wrestler has been bracketed
     */
    public getIsBracketed(eventId: string, wrestlerId: string): Observable<boolean> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, wrestlerId, [])}/is-bracketed`;

        return this.http.get<boolean>(apiRequestUrl)
            .pipe(map((response: boolean) => response));
    }

    /**
     * Add or update the wrestler
     */
    public save(wrestler: Wrestler, includes: string[] = null): Observable<Wrestler> {
        let response: Observable<IJsonApiResponse>;
        const queryParams: string[] = this.appendGetFields(includes);
        const wrestlerId: string = !wrestler.isNew ? wrestler.id : null;
        const apiRequestUrl: string = this.getApiUrl(wrestler.eventId, wrestlerId, includes, queryParams);

        if (!wrestler.isNew) {
            // Update existing Wrestler
            response = this.http.patch(apiRequestUrl, wrestler.serialize());
        } else {
            // Add a new Wrestler
            response = this.http.post(apiRequestUrl, wrestler.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) =>
                    (new Wrestler(wrestler.eventId)).deserialize(response, response.included)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified wrestler.
     */
    public delete(eventId: string, wrestlerId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, wrestlerId);
        return this.http.delete(apiRequestUrl)
            .pipe(
                map(() => true),
                finalize(() => this.clearCache()),
            );
    }

    private appendGetFields(includes: string[] = [], queryParams: string[] = []): string[] {
        includes = includes || [];
        queryParams = queryParams || [];

        // Only fetch specific fields
        if (queryParams.filter((x: string) => x.indexOf('fields[wrestler]') !== -1).length === 0) {
            queryParams.push(`fields[wrestler]=${Wrestler.getFields().join(',')}`);
        }

        if (includes.filter((x: string) => x.indexOf(WrestlerService.INCLUDE_TEAM) !== -1).length > 0 &&
            queryParams.filter((x: string) => x.indexOf('fields[team]') !== -1).length === 0) {
            queryParams.push(`fields[team]=${Team.getFields().join(',')}`);
        }

        if (includes.filter((x: string) => x.indexOf(WrestlerService.INCLUDE_DIVISION) !== -1).length > 0 &&
            queryParams.filter((x: string) => x.indexOf('fields[divisions]') !== -1).length === 0) {
            queryParams.push(`fields[division]=${Division.getFields().join(',')}`);
        }

        return queryParams;
    }

    /**
     * Link to wrestlers to their identity profile if possible
     */
    public linkProfiles(eventId: string, linkAndCreateByNameLocation: boolean): Observable<boolean> {
        const queryParams: string[] = [`linkAndCreateByNameLocation=${linkAndCreateByNameLocation}`];
        const apiRequestUrl: string = this.getApiUrl(eventId, 'link-profiles', null, queryParams);

        return this.http.patch(apiRequestUrl, null)
            .pipe(
                map(() => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Link to wrestlers to their identity profile if possible
     */
    public isLinkingProfiles(eventId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, 'is-linking-profiles');

        return this.http.get<ILinkingProfiles>(apiRequestUrl, null)
            .pipe(
                map((response: ILinkingProfiles) => response.isLinkingInProgress),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        eventId: string,
        wrestlerId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/wrestlers/`;

        if (wrestlerId) {
            url += wrestlerId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}

export interface ILinkingProfiles {
    isLinkingInProgress: boolean;
}
